/**
 * CMMD's cloud-project API record → the catalog row Forge renders.
 *
 * The rule the whole mapping exists to hold: nothing is fabricated. A project
 * CMMD reports no runtime for comes back with `runtimeId`, `environmentId` and
 * `cmmdEnvironmentId` all null and the `registered` status, so every consumer
 * has to handle "never provisioned" explicitly instead of mistaking it for a
 * stopped machine. Each helper below is one field group that would otherwise be
 * re-derived, differently, at whichever call site needed it next.
 */
import type {
  CmmdForgeCloudEnvironmentRow,
  CmmdForgeCloudProjectRow,
} from "../environments/primary/cloudProjectCatalog";
import {
  mapCmmdStatusToRuntimeStatus,
  resolveCloudProjectDisplayName,
  UNPROVISIONED_CLOUD_PROJECT_STATUS,
  type ForgeRuntimeStatus,
} from "./forgeRuntimeStatus";
import { buildCmmdRuntimeEnvironmentId } from "../environments/cmmdRuntimeEnvironmentId";
import { projectCloudEnvironmentLifecycle } from "../environments/primary/cloudEnvironmentLifecycleProjection";
// Type-only, so it is erased and no runtime cycle exists: the resource owns the
// row shape, this module owns how one is built from CMMD's record.
import type {
  CloudEnvironmentCatalogRow,
  CloudProjectCatalogRow,
} from "./cmmdForgeRuntimeListTypes";

/**
 * Runtime state → row status. Absent runtime means the project is registered
 * and has never been provisioned, which is its own state rather than a
 * degraded/failed one.
 */
function resolveCloudProjectStatus(
  runtime: CmmdForgeCloudProjectRow["runtime"],
): ForgeRuntimeStatus {
  if (!runtime) return UNPROVISIONED_CLOUD_PROJECT_STATUS;
  return mapCmmdStatusToRuntimeStatus(runtime.status, runtime.readinessPhase ?? undefined);
}

/**
 * The runtime's cloned repo wins when present (it is what is actually on disk);
 * before the first provision, fall back to the project's canonical repo.
 */
function resolveCloudProjectRepo(record: CmmdForgeCloudProjectRow): string | null {
  return record.runtime?.githubRepoFullName ?? record.githubRepoFullName ?? null;
}

/** Identity fields that exist whether or not the project has been provisioned. */
function cloudProjectIdentityFields(record: CmmdForgeCloudProjectRow, repo: string | null) {
  return {
    cmmdOrganizationId: record.organizationId,
    cmmdProjectId: record.projectId,
    projectName: resolveCloudProjectDisplayName({
      shortName: record.shortName ?? null,
      projectName: record.projectName ?? null,
      githubRepoFullName: repo,
      cmmdProjectId: record.projectId,
    }),
    githubRepoFullName: repo,
    iconUrl: record.iconUrl ?? null,
    icon: record.icon ?? null,
  };
}

export function cloudProjectRuntimeFields(
  runtime: CmmdForgeCloudProjectRow["runtime"],
): Pick<
  CloudProjectCatalogRow,
  | "runtimeId"
  | "workspaceRoot"
  | "status"
  | "desiredState"
  | "environmentId"
  | "cmmdEnvironmentId"
  | "maintenance"
> {
  if (!runtime) {
    return {
      runtimeId: null,
      workspaceRoot: null,
      status: UNPROVISIONED_CLOUD_PROJECT_STATUS,
      desiredState: null,
      environmentId: null,
      cmmdEnvironmentId: null,
    };
  }
  return {
    runtimeId: runtime.runtimeId,
    workspaceRoot: runtime.workspaceRoot ?? null,
    status: resolveCloudProjectStatus(runtime),
    desiredState: readCloudProjectDesiredState(runtime.desiredState),
    environmentId: buildCmmdRuntimeEnvironmentId(runtime.runtimeId),
    // Passed through, never synthesized. A CMMD that has not deployed the field
    // yields null, and null is the honest answer: the legacy form is already
    // carried in `environmentId` above, and duplicating it here would make a
    // Sprite-scoped string look like a durable one to everything downstream.
    cmmdEnvironmentId: runtime.environmentId ?? null,
    ...(runtime.maintenance !== undefined ? { maintenance: runtime.maintenance } : {}),
  };
}

/**
 * Narrow CMMD's `desiredState` string to the three values that mean anything
 * here, and NULL for everything else.
 *
 * Unknown must not read as `"stopped"`: that is the one value which withholds a
 * resume, so guessing it would strand a healthy sprite behind a Start button.
 */
export function readCloudProjectDesiredState(
  raw: string | null | undefined,
): CloudProjectCatalogRow["desiredState"] {
  return raw === "running" || raw === "stopped" || raw === "destroyed" ? raw : null;
}

function cloudProjectRowFromApiRecord(record: CmmdForgeCloudProjectRow): CloudProjectCatalogRow {
  return {
    ...cloudProjectIdentityFields(record, resolveCloudProjectRepo(record)),
    registeredAt: record.registeredAt ?? null,
    ...cloudProjectRuntimeFields(record.runtime),
    removal: record.removal ?? null,
    resolvedRef: null,
  } satisfies CloudProjectCatalogRow;
}

export function cloudProjectRowsFromApiRecords(
  records: readonly CmmdForgeCloudProjectRow[],
): CloudProjectCatalogRow[] {
  return records.map(cloudProjectRowFromApiRecord);
}

export function cloudEnvironmentRowsFromApiRecords(
  records: readonly CmmdForgeCloudEnvironmentRow[],
): CloudEnvironmentCatalogRow[] {
  return records.map((record) => ({
    runtimeId: record.runtime.runtimeId,
    cmmdOrganizationId: record.organizationId,
    cmmdUserId: record.userId,
    displayName: record.displayName ?? null,
    userFirstName: record.userFirstName ?? null,
    environmentId: buildCmmdRuntimeEnvironmentId(record.runtime.runtimeId),
    cmmdEnvironmentId: record.runtime.environmentId ?? null,
    status: resolveCloudProjectStatus(record.runtime),
    desiredState: readCloudProjectDesiredState(record.runtime.desiredState),
    ...(record.runtime.maintenance !== undefined
      ? { maintenance: record.runtime.maintenance }
      : {}),
    lifecycle: projectCloudEnvironmentLifecycle(record.lifecycle),
  }));
}
